import posthog from 'posthog-js';

export const ANALYTICS_EVENTS = {
  sessionStarted: 'session_started',
  activityCompleted: 'activity_completed',
  activityUndone: 'activity_undone',
  skillUnlocked: 'skill_unlocked',
  certificateGenerated: 'certificate_generated',
  cvSentenceGenerated: 'cv_sentence_generated',
  cvSentenceCopied: 'cv_sentence_copied',
  aiPromptCopied: 'ai_prompt_copied',
  careerViewed: 'career_viewed',
  certificateDownloaded: 'certificate_downloaded',
  certificateShared: 'certificate_shared',
  searchUsed: 'search_used',
  searchResultClicked: 'search_result_clicked',
} as const;

export type AnalyticsEvent = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

// posthog is only init'd in production with a key (see main.tsx); everywhere
// else these calls are no-ops so feature code can fire events unconditionally.
const isReady = () => posthog.__loaded === true;

// Whether analytics actually loaded — gates UI such as the consent banner.
export const isAnalyticsActive = isReady;

export function trackEvent(event: AnalyticsEvent, properties?: Record<string, unknown>) {
  if (!isReady()) return;
  posthog.capture(event, properties);
}

let isUserIdentified = false;

export function identifyUser(user: { id: string }) {
  if (!isReady()) return;
  posthog.identify(user.id);
  isUserIdentified = true;
}

export function resetAnalyticsIdentity() {
  isUserIdentified = false;
  clearSessionStarted();
  if (!isReady()) return;
  posthog.reset();
}

const SESSION_STARTED_KEY = 'analytics_session_started';

// In-memory backstop so a blocked sessionStorage can't let session_started fire
// more than once per tab (the storage write below may silently fail).
let sessionStartedFired = false;

function hasFiredSessionStarted(): boolean {
  if (sessionStartedFired) return true;
  try {
    return sessionStorage.getItem(SESSION_STARTED_KEY) === 'true';
  } catch {
    return false;
  }
}

function markSessionStarted() {
  sessionStartedFired = true;
  try {
    sessionStorage.setItem(SESSION_STARTED_KEY, 'true');
  } catch {
    // storage blocked — the in-memory flag above still caps it to once per tab.
  }
}

function clearSessionStarted() {
  sessionStartedFired = false;
  try {
    sessionStorage.removeItem(SESSION_STARTED_KEY);
  } catch {
    // ignore — nothing persisted to clear.
  }
}

export function trackSessionStartedOnce() {
  if (!isReady() || !isUserIdentified || !posthog.has_opted_in_capturing()) return;
  if (hasFiredSessionStarted()) return;
  markSessionStarted();
  posthog.capture(ANALYTICS_EVENTS.sessionStarted);
}

// --- Cookie consent (GDPR): capturing stays off until the user accepts. ---

const CONSENT_STORAGE_KEY = 'cookie_consent';

export type ConsentChoice = 'accepted' | 'rejected';

export function getStoredConsent(): ConsentChoice | null {
  try {
    const value = localStorage.getItem(CONSENT_STORAGE_KEY);
    return value === 'accepted' || value === 'rejected' ? value : null;
  } catch {
    return null;
  }
}

export function setConsent(choice: ConsentChoice) {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, choice);
  } catch {
    // storage blocked — still apply the choice for this session below.
  }
  if (!isReady()) return;
  if (choice === 'accepted') {
    posthog.opt_in_capturing();
    trackSessionStartedOnce();
  } else {
    posthog.opt_out_capturing();
  }
}

export function applyStoredConsent() {
  if (!isReady()) return;
  if (getStoredConsent() === 'accepted') posthog.opt_in_capturing();
  else posthog.opt_out_capturing();
}
